import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import type { DemographicKey } from '@/types'

interface BreakSelectorProps {
  // Include scatterplot-specific breaks (media, industry, identity_verymuch)
  includeScatterplotBreaks?: boolean
}

// Breaks that only appear on scatterplot page
const SCATTERPLOT_ONLY_BREAKS = ['media', 'industry', 'identity_verymuch']
const ALWAYS_EXCLUDED = ['region']

export function BreakSelector({ includeScatterplotBreaks = false }: BreakSelectorProps) {
  const { state, setBreak } = useApp()
  const { filters, data } = state
  const selectedBreak = filters.selectedBreak

  // Get filtered break values (filtered by province when applicable)
  const { breakValues, isFiltered, totalValues } = useFilteredBreakValues()

  // Get breaks from data - sorted by sortOrder
  // Filter out breaks that don't make sense for bar charts:
  // - 'region': duplicates the Province dropdown
  // - 'media', 'industry', 'identity_verymuch': multi-select breaks that are confusing on bar charts (only useful on scatterplot)
  const excludedBreaks = includeScatterplotBreaks
    ? ALWAYS_EXCLUDED
    : [...ALWAYS_EXCLUDED, ...SCATTERPLOT_ONLY_BREAKS]

  const breakOptions = data?.breaks
    .slice()
    .filter(b => !excludedBreaks.includes(b.id))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(b => ({
      id: b.id as DemographicKey,
      name: b.name,
    })) || []

  const handleBreakTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const breakType = e.target.value as DemographicKey
    // Preserve scroll position when changing breaks
    const scrollY = window.scrollY
    setBreak(breakType)
    // Restore scroll position after React re-renders
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY)
    })
  }

  return (
    <div className="flex items-center gap-4" role="group" aria-label="Data breakdown controls">
      <div className="flex items-center gap-2">
        <label
          htmlFor="break-select"
          className="text-sm font-medium text-slate-600 dark:text-slate-300"
        >
          Select Break:
        </label>
        <select
          id="break-select"
          value={selectedBreak}
          onChange={handleBreakTypeChange}
          aria-label="Select demographic breakdown"
          aria-describedby={breakValues.length > 0 ? "break-count" : undefined}
          className="px-3 py-1.5 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none"
        >
          {breakOptions.map(option => (
            <option
              key={option.id}
              value={option.id}
            >
              {option.name}
            </option>
          ))}
        </select>
      </div>

      {/* Show break values count when a break is selected */}
      {selectedBreak !== 'total' && breakValues.length > 0 && (
        <span id="break-count" className="text-xs text-slate-500 dark:text-slate-400">
          ({isFiltered ? `${breakValues.length} of ${totalValues}` : breakValues.length} groups)
        </span>
      )}
    </div>
  )
}

// Export helper for other components to use display mapping
export function useBreakDisplayMap() {
  const { state } = useApp()
  const { filters, data } = state
  const breakDef = data?.breaks.find(b => b.id === filters.selectedBreak)
  const displayMap = (breakDef as { displayMap?: Record<string, string> })?.displayMap || {}

  return (value: string) => displayMap[value] || value
}
